import Select from "components/shared/form/select";
import { plainDate } from "helpers/date";
import {
    bidPointTypesEnum,
    extendedPropertyFlags,
    extendedPropertyFlagsEnum,
    simplifiedTypeCodesEnum,
} from "helpers/enums";
import { flagVal } from "helpers/system";
import { requiredField } from "helpers/validators";
import flattenDepth from "lodash/flattenDepth";
import moment from "moment";
import React from "react";

export function getInitialStateFromConfig(config, options = {}) {
    const { isExtendedProp } = options;
    const initialState = {};
    for (let key in config) {
        const item = config[key];
        if (isExtendedProp) {
            key = key.slice(key.indexOf(".") + 1, key.length);
        }
        switch (item.type) {
            case "checkbox":
                initialState[key] = item.value || false;
                break;
            case "object":
                initialState[key] = {};
                break;
            default:
                initialState[key] = item.value || item.value === 0 ? item.value : "";
                break;
        }
    }
    return initialState;
}

export function defaultExtendedPropValue(typeCode) {
    switch (typeCode) {
        case simplifiedTypeCodesEnum.Integer:
            return 0;
        case simplifiedTypeCodesEnum.Float:
            return 0;
        case simplifiedTypeCodesEnum.String:
            return "-";
        case simplifiedTypeCodesEnum.DateTime:
        case simplifiedTypeCodesEnum.DateTimeWithoutTimezone:
            return moment().format();
        case simplifiedTypeCodesEnum.Boolean:
            return false;
        default:
            return "-";
    }
}

export function serializeReq(item, config = {}) {
    if (Array.isArray(item) && item.length) {
        return item.map((arrItem) => serializeReq(arrItem, config)).filter((arrItem) => isFilledObjOrItem(arrItem));
    } else if (isFilledObj(item)) {
        const formatted = {};
        for (const key in item) {
            const prop = item[key];
            if (config[key]?.nullIfEmpty && (prop === "" || prop === null)) {
                formatted[key] = null;
            } else if (config[key]?.nullable || (prop !== "" && prop !== undefined)) {
                formatted[key] = serializeReq(item[key], config[key]);
            }
        }
        return formatted;
    }
    return item;
}

export function parseManualValues(value = "") {
    const stringValue = `${value || ""}`;
    if (!stringValue.trim()) {
        return null;
    }
    try {
        const parsed = JSON.parse(stringValue);
        if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
            return false;
        }
        return parsed;
    } catch (_e) {
        return false;
    }
}

export function isFilledObj(obj) {
    return typeof obj === "object" && obj && Object.keys(obj).length;
}

function isFilledObjOrItem(obj) {
    if (obj && typeof obj === "object") {
        return Object.keys(obj).length;
    }
    return !!obj;
}

export function formatInitial(res, initial, action) {
    const formatted = {};
    for (const key in initial) {
        formatted[key] = res[key] || res[key] === 0 || action === "edit" ? res[key] : initial[key];
    }
    return formatted;
}

export function formatExtendedProperties({ extendedProperties, ...res }, config) {
    extendedProperties = extendedProperties || [];
    const getVal = (key) => {
        const option = extendedProperties.find((item) => item.propertyName === key);
        return option ? option.value : "";
    };
    const props = Object.values(config).reduce(
        (acc, prop) => ({
            ...acc,
            [prop.inputProps.name]: formatPropValue(prop.typeCode, getVal(prop.inputProps.name)),
        }),
        {},
    );
    return { ...res, extendedProperties: props };
}

export function formatExtendedPropertiesForSave(obj, config) {
    return Object.entries(obj.extendedProperties || {}).map(({ 0: propertyName, 1: value }) => {
        const typeCode = config[`extendedProperties.${propertyName}`].typeCode;
        const formattedValue = () => {
            switch (typeCode) {
                case simplifiedTypeCodesEnum.DateTime:
                    return value ? moment(value).format() : value;
                case simplifiedTypeCodesEnum.DateTimeWithoutTimezone:
                    return value ? moment(value).format("YYYY-MM-DDTHH:mm") : value;
                default:
                    return value;
            }
        };
        return { propertyName, value: formattedValue() };
    });
}

export function createRequestForUploadFile(name, file, fileName) {
    const formData = new FormData();
    if (name) {
        formData.append(name, file, fileName);
    }
    return {
        body: formData,
        method: "POST",
        headers: {
            ContentType: "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "X-Device-Id": localStorage.getItem("deviceId"),
        },
    };
}

const valueByKey = (item, name) => {
    const index = name.indexOf(".");
    const arr = index >= 0 ? [name.slice(0, index), name.slice(index + 1, name.length)] : [name];
    return arr.reduce((o, key) => (o ? o[key] : undefined), item);
};

export function validateForm(config, data) {
    const validatorsData = flattenDepth(extractValidators(config), 2);
    return !validatorsData.some(({ name, validators, prefix }) => {
        return validators.some((validator) => {
            if (prefix) {
                const objValue = valueByKey(data, prefix);
                if (Array.isArray(objValue)) {
                    return objValue.some((item) => !!validator(valueByKey(item, name), item));
                } else {
                    return !!validator(valueByKey(objValue, name), data);
                }
            } else {
                return !!validator(valueByKey(data, name), data);
            }
        });
    });
}

export function logInvalidKeys(config, data) {
    const invalidKeys = [];
    const validatorsData = flattenDepth(extractValidators(config), 2);
    validatorsData.forEach(({ name, validators, prefix }) => {
        validators.forEach((validator) => {
            if (prefix) {
                const objValue = valueByKey(data, prefix);
                if (Array.isArray(objValue)) {
                    objValue.forEach((item) => {
                        if (validator(valueByKey(item, name), item)) {
                            invalidKeys.push({
                                prefix,
                                name,
                                value: valueByKey(item, name),
                                validator: validator.name,
                            });
                        }
                    });
                } else {
                    if (validator(valueByKey(objValue, name), data)) {
                        invalidKeys.push({
                            prefix,
                            name,
                            value: valueByKey(objValue, name),
                            validator: validator.name,
                        });
                    }
                }
            } else {
                if (validator(valueByKey(data, name), data)) {
                    invalidKeys.push({ prefix, name, value: valueByKey(data, name), validator: validator.name });
                }
            }
        });
    });
    console.table(invalidKeys);
}

function extractValidators(config, prefix = null) {
    return Object.entries(config)
        .filter((item) => item[1].validators || item[1].config)
        .map((item) => {
            if (item[1].validators) {
                return { name: item[0], validators: item[1].validators, prefix };
            } else {
                // TODO do it more flexible, remove `extendedProperties` check
                return extractValidators(
                    item[1].config,
                    item[0] === "extendedProperties" && !prefix ? null : prefix || item[0],
                );
            }
        });
}

export function renderRelatedFields({ defProps, props, renderField, renderSelect }) {
    if (typeof defProps.value === "number") {
        const item = props.options.find((item) => item.value === defProps.value);
        if (item) {
            const { value } = item.options.find((option) => option.id === item.value);
            const { key, ...selectProps } = { ...defProps, ...props };
            return (
                <div className="inputs-group--full-list">
                    <Select {...selectProps} key={key} />
                    {value.map((val) => (val.inputProps.type === "select" ? renderSelect(val) : renderField(val)))}
                </div>
            );
        }
    }
    const { key, ...selectProps } = { ...defProps, ...props };
    return <Select {...selectProps} key={key} />;
}

const formatPropValue = (typeCode, defaultValue) => {
    switch (typeCode) {
        case simplifiedTypeCodesEnum.Integer:
            return defaultValue ? parseInt(defaultValue) : "";
        case simplifiedTypeCodesEnum.Float:
            return defaultValue ? parseFloat(defaultValue) : "";
        case simplifiedTypeCodesEnum.String:
            return defaultValue || "";
        case simplifiedTypeCodesEnum.Boolean:
            return defaultValue === "true";
        case simplifiedTypeCodesEnum.DateTime:
        case simplifiedTypeCodesEnum.DateTimeWithoutTimezone:
            return defaultValue ? plainDate(defaultValue) : defaultValue;
        default:
            return defaultValue || "";
    }
};

const requiredFlagVal = flagVal(extendedPropertyFlags, extendedPropertyFlagsEnum.Required);
const hiddenFlagVal = flagVal(extendedPropertyFlags, extendedPropertyFlagsEnum.Invisible);
const showInEmptyMileageBidsFlagVal = flagVal(
    extendedPropertyFlags,
    extendedPropertyFlagsEnum.ShowInEmptyMileageBids,
);

export const buildConfigByProperties = (properties, options = { isRelation: false }) =>
    properties.reduce((res, prop) => {
        const flags = prop[options.isRelation ? "intRelationFlags" : "intFlags"];
        const isRequired = (flags & requiredFlagVal) === requiredFlagVal;
        const isHidden = (flags & hiddenFlagVal) === hiddenFlagVal;
        const showInEmptyMileageBids =
            showInEmptyMileageBidsFlagVal > 0 &&
            (flags & showInEmptyMileageBidsFlagVal) === showInEmptyMileageBidsFlagVal;
        res[`extendedProperties.${prop.propertyName}`] = {
            label: prop.displayName,
            inputProps: {
                name: prop.propertyName,
                type: [simplifiedTypeCodesEnum.Integer, simplifiedTypeCodesEnum.Float].includes(prop.typeCode)
                    ? "number"
                    : "text",
            },
            value: formatPropValue(prop.typeCode, prop.defaultValue),
            validators: isRequired
                ? [(value, data) => (data.type !== bidPointTypesEnum.ServicePoint ? requiredField(value) : "")]
                : [],
            required: isRequired,
            isHidden,
            showInEmptyMileageBids,
            typeCode: prop.typeCode,
            order: prop.order,
            fieldWrapper: "col-sm-8 col-md-5",
            wrapper: "col-sm-8 col-md-5",
            tooltip: prop.description,
            ...(prop.typeCode === simplifiedTypeCodesEnum.Boolean ? { type: "checkbox" } : {}),
        };
        return res;
    }, {});
